import { z } from "zod";
import {
  CORRECTION_REASON_MAX,
  businessIdempotencyKeySchema,
  correctionHistoryResponseSchema,
  correctionResponseSchema,
  positiveMinorString,
} from "./ledger-correction.js";
import { evidenceAttachmentSchema, evidenceKeySchema } from "./contribution-evidence.js";

/**
 * 🔴 US-AE4.F7-1007 (@UC9951 · QĐ người dùng 05/10/2026) — "SỬA THU": SUPER_ADMIN sửa SỐ TIỀN của MỘT lần ghi tiền
 * vào quỹ đang hiệu lực, hộ người dùng. `POST /v1/admin/funds/:id/transactions/:txId/amount-correction`.
 *
 * Đường sửa là `CorrectionService.correct()` CỦA APP (đảo + ghi lại số đúng trong một giao dịch, khoản đóng tính lại
 * theo sổ) — hợp đồng này chỉ là cửa vào của lane admin, KHÔNG phải engine thứ hai. Vì thế response TÁI DÙNG nguyên
 * `correctionResponseSchema`, và body chỉ có SỐ TIỀN: admin không đổi người nhận (`.strict()` chặn `payeeMemberId`).
 */

/**
 * Loại lần ghi được sửa — closed-set. `CONTRIBUTION` = lần đóng quỹ (gắn `member_dues`), `SPONSORSHIP_MEMBER` = đóng
 * góp khác (dòng "income.recorded" của Sổ quỹ). 🔴 `PAY_ON_BEHALF` CỐ Ý VẮNG: nộp hộ kéo theo nợ người↔người, app chưa
 * có luồng đó — ngoài phạm vi (QĐ 05/10).
 */
export const ADMIN_INCOME_CORRECTABLE_TX_TYPES = ["CONTRIBUTION", "SPONSORSHIP_MEMBER"] as const;
export type AdminIncomeCorrectableTxType = (typeof ADMIN_INCOME_CORRECTABLE_TX_TYPES)[number];

/** Lần ghi không thuộc loại sửa được ở đây (nộp hộ, khoản chi, bút toán đảo, …) — 422, 0 bút toán. */
export const ADMIN_INCOME_CORRECTION_UNSUPPORTED_CODE =
  "ADMIN_INCOME_CORRECTION_UNSUPPORTED" as const;

/**
 * Số mới TRÙNG số đang ghi — 422, 0 bút toán. Không chặn thì máy vẫn đảo rồi ghi lại đúng số cũ: hai bút toán không đổi
 * gì nằm vĩnh viễn trong sổ append-only, và lịch sử sửa có một dòng "sửa" mà không ai sửa gì.
 */
export const ADMIN_INCOME_CORRECTION_UNCHANGED_CODE = "ADMIN_INCOME_CORRECTION_UNCHANGED" as const;

/**
 * 🔴 US-AE4.F7-1039 (@UC9951 · QĐ người dùng 30/10/2026) — MỘT form sửa: số tiền đúng và/hoặc tệp đính kèm mới, chung một lý do. Ít nhất
 * một trong hai phải có (không có gì để sửa ⇒ 400 ở biên). Đổi tệp = 0 bút toán; sửa số = `CorrectionService.correct()` như cũ.
 */
export const adminIncomeCorrectionRequestSchema = z
  .object({
    /** Số ĐÚNG, string minor > 0. Vắng ⇒ không sửa số. */
    amountMinor: positiveMinorString.optional(),
    /** Khoá tệp đã upload ở bước 1 (`POST /v1/admin/funds/:id/contribution-evidence`). Vắng ⇒ không đổi tệp. */
    evidenceKey: evidenceKeySchema.optional(),
    /** Bắt buộc — trim trước khi đo: chuỗi toàn khoảng trắng không phải một lý do. */
    reason: z.string().trim().min(1, "Lý do chỉnh sửa là bắt buộc").max(CORRECTION_REASON_MAX),
    /** Khoá tất định theo NỘI DUNG (client băm) — gửi lại cùng khoá ⇒ đúng MỘT lần sửa. */
    correctionKey: businessIdempotencyKeySchema,
  })
  .strict()
  .refine((v) => v.amountMinor !== undefined || v.evidenceKey !== undefined, {
    message: "Cần số tiền đúng hoặc tệp đính kèm mới",
    path: ["amountMinor"],
  });
export type AdminIncomeCorrectionRequest = z.infer<typeof adminIncomeCorrectionRequestSchema>;

/**
 * Response — hai hình dạng, phân biệt bằng việc có con trỏ sửa hay không:
 *  - có sửa số ⇒ ĐÚNG `correctionResponseSchema` của app + `txId` (= `correctedTxId`, lần ghi đang hiện) + `attachmentChanged`;
 *  - chỉ đổi tệp ⇒ `txId` (lần ghi giữ nguyên) + `attachmentChanged: true`.
 */
export const adminIncomeCorrectionResponseSchema = z.union([
  correctionResponseSchema.extend({ txId: z.string().uuid(), attachmentChanged: z.boolean() }).strict(),
  z.object({ txId: z.string().uuid(), attachmentChanged: z.literal(true) }).strict(),
]);
export type AdminIncomeCorrectionResponse = z.infer<typeof adminIncomeCorrectionResponseSchema>;

/** Lịch sử ở màn "Chi tiết hoạt động" admin = lịch sử của app + tệp HIỆN TẠI của lần ghi (form cần thấy tệp đang có). */
export const adminIncomeHistoryResponseSchema = correctionHistoryResponseSchema
  .extend({ attachment: evidenceAttachmentSchema.nullable() })
  .strict();
export type AdminIncomeHistoryResponse = z.infer<typeof adminIncomeHistoryResponseSchema>;
